import {useEffect, useMemo, type CSSProperties, type ReactNode} from "react";

import {useSetting} from "../../../contexts/setting.js";
import {drawnClientTemplates} from "../../../data/templates.js";
import {useTemplateSelection} from "../../../hooks/useTemplateSelection";

import adChoiceDot from "./assets/ad-choice-dot.svg";
import adChoiceMenuDot from "./assets/ad-choice-menu-dot.svg";
import adChoiceOutline from "./assets/ad-choice-outline.svg";
import adChoicePlay from "./assets/ad-choice-play.svg";
import audio01 from "./assets/cnn-audio-01.preview.jpg";
import audio02 from "./assets/cnn-audio-02.preview.jpg";
import audio03 from "./assets/cnn-audio-03.preview.jpg";
import audio04 from "./assets/cnn-audio-04.preview.jpg";
import audio05 from "./assets/cnn-audio-05.preview.jpg";
import audio06 from "./assets/cnn-audio-06.preview.jpg";
import audio07 from "./assets/cnn-audio-07.preview.jpg";
import extraArchitecture from "./assets/cnn-extra-architecture.preview.jpg";
import extraSports from "./assets/cnn-extra-sports.preview.jpg";
import extraTech from "./assets/cnn-extra-tech.preview.jpg";
import lowerCulture from "./assets/cnn-lower-culture.preview.jpg";
import lowerScience from "./assets/cnn-lower-science.preview.jpg";
import lowerUs from "./assets/cnn-lower-us.preview.jpg";
import lowerWorld from "./assets/cnn-lower-world.preview.jpg";
import more01 from "./assets/cnn-more-01.preview.jpg";
import more02 from "./assets/cnn-more-02.preview.jpg";
import more03 from "./assets/cnn-more-03.preview.jpg";
import more04 from "./assets/cnn-more-04.preview.jpg";
import more05 from "./assets/cnn-more-05.preview.jpg";
import more06 from "./assets/cnn-more-06.preview.jpg";
import more07 from "./assets/cnn-more-07.preview.jpg";
import more08 from "./assets/cnn-more-08.preview.jpg";
import more09 from "./assets/cnn-more-09.preview.jpg";
import more10 from "./assets/cnn-more-10.preview.jpg";
import more11 from "./assets/cnn-more-11.preview.jpg";
import more12 from "./assets/cnn-more-12.preview.jpg";
import newsHero from "./assets/cnn-news-hero.preview.jpg";
import newsLeft01 from "./assets/cnn-news-left-01.preview.jpg";
import newsLeft02 from "./assets/cnn-news-left-02.preview.jpg";
import newsLeft03 from "./assets/cnn-news-left-03.preview.jpg";
import sportAudio01 from "./assets/cnn-sport-audio-01.preview.jpg";
import sportAudio02 from "./assets/cnn-sport-audio-02.preview.jpg";
import sportAudio03 from "./assets/cnn-sport-audio-03.preview.jpg";
import sportAudio04 from "./assets/cnn-sport-audio-04.preview.jpg";
import sportAudio05 from "./assets/cnn-sport-audio-05.preview.jpg";
import sportAudio06 from "./assets/cnn-sport-audio-06.preview.jpg";
import sportAudio07 from "./assets/cnn-sport-audio-07.preview.jpg";
import techLaptop from "./assets/cnn-tech-laptop.preview.jpg";
import techChips from "./assets/cnn-tech-chips.preview.jpg";
import techPhone from "./assets/cnn-tech-phone.preview.jpg";
import world02 from "./assets/cnn-world-02.preview.jpg";
import world03 from "./assets/cnn-world-03.preview.jpg";
import world05 from "./assets/cnn-world-05.preview.jpg";
import world07 from "./assets/cnn-world-07.preview.jpg";
import world08 from "./assets/cnn-world-08.preview.jpg";
import world13 from "./assets/cnn-world-13.preview.jpg";
import world14 from "./assets/cnn-world-14.preview.jpg";
import world18 from "./assets/cnn-world-18.preview.jpg";
import world19 from "./assets/cnn-world-19.preview.jpg";
import world20 from "./assets/cnn-world-20.preview.jpg";
import worldPeopleLost from "./assets/cnn-world-people-lost.preview.png";
import "./index.css";

const GDN_TEMPLATE_IDS = [
  "BillboardAds",
  "Leaderboard970Ads",
  "Leaderboard728Ads",
  "Leaderboard468Ads",
  "GdnJapanAds",
  "InlineRectangleAds",
  "LargeRectangleAds",
  "HalfPageAds",
  "WideSkyscraperAds",
  "GdnResponsive1200x628Ads",
  "GdnResponsive1200x1200Ads",
] as const;

type GdnTemplateId = (typeof GDN_TEMPLATE_IDS)[number];

type ClientTemplate = {
  id: string;
  width: number;
  height: number;
  contentTypes?: readonly string[];
  layouts?: readonly number[];
  getComponent: () => ReactNode;
};

type AdRenderer = (
  id: GdnTemplateId,
  className?: string,
  instanceName?: string
) => ReactNode;

type Story = {
  image: string;
  title: string;
  section?: string;
  summary?: string;
  duration?: string;
};

const DISPLAY_SCALE: Record<GdnTemplateId, number> = {
  BillboardAds: 0.5,
  Leaderboard970Ads: 0.5,
  Leaderboard728Ads: 0.5,
  Leaderboard468Ads: 0.5,
  GdnJapanAds: 0.5,
  InlineRectangleAds: 0.5,
  LargeRectangleAds: 0.5,
  HalfPageAds: 0.5,
  WideSkyscraperAds: 0.5,
  GdnResponsive1200x628Ads: 0.415,
  GdnResponsive1200x1200Ads: 0.415,
};

const TEMPLATE_ID_SET = new Set<string>(GDN_TEMPLATE_IDS);

const moreStories: Story[] = [
  {image: more01, duration: "6:13", title: "Crisis in Hormuz a net win for China for now, says consulting firm"},
  {image: more02, duration: "2:35", title: "Venezuelans dig by hand as fuel shortages stall rescue efforts"},
  {image: more03, title: "Venezuelans deported by the US hours before the deadly earthquake struck are missing"},
  {image: more04, duration: "1:53", title: "International manhunt underway after bomb attack wounds Ukrainian tycoon"},
  {image: more05, title: "Manhunt after Monaco bomb attack wounds Ukrainian tycoon in ‘attempted assassination’"},
  {image: more06, duration: "1:59", title: "Ukraine launches major drone attack on Moscow"},
  {image: more07, duration: "2:10", title: "Rescue crews stop at nothing to find survivors days after the Venezuela earthquakes"},
  {image: more08, duration: "0:25", title: "12-year-old boy pulled from rubble in Venezuela"},
  {image: more09, duration: "1:00", title: "‘Neil the seal’ takes over Tasmanian towns"},
  {image: more10, duration: "1:17", title: "Families plead for information on deported Venezuelans in hotel collapse"},
  {image: more11, duration: "2:39", title: "‘We’re not prepared’: As Venezuela shakes, years of mismanagement come into focus"},
  {image: more12, duration: "4:41", title: "Major questions loom four months after deadly strike on Iran elementary school"},
];

const recommendedAudio: Story[] = [
  {image: audio01, section: "The Global Story", title: "Abdi’s American Dream", duration: "32 mins"},
  {image: audio02, section: "Global News Podcast", title: "Supreme Court hands Trump a big win and several defeats", duration: "25 mins"},
  {image: audio03, section: "More or Less", title: "Examining the numbers behind Andy Burnham’s ‘Manchester Miracle’", duration: "28 mins"},
  {image: audio04, section: "Witness History", title: "Rosa Parks and the Montgomery Bus Boycott", duration: "10 mins"},
  {image: audio05, section: "The Documentary Podcast", title: "The Food Chain: Can I save the family restaurant?", duration: "26 mins"},
  {image: audio06, section: "The Interview", title: "Katie Sadleir, Commonwealth Games: They are still relevant", duration: "23 mins"},
  {image: audio07, section: "Business Daily", title: "Founders: Charlotte Tilbury on beauty, business and confidence", duration: "18 mins"},
];

const sportAudio: Story[] = [
  {image: sportAudio01, section: "More than the Score", title: "World Cup 2026: Cape Verde prepare to face Messi’s Argentina", duration: "15 mins"},
  {image: sportAudio02, section: "Football Daily", title: "World Cup: Thomas Tuchel & Jordan Pickford Ahead Of DR Congo Match", duration: "22 mins"},
  {image: sportAudio03, section: "Football Daily", title: "World Cup: Tim Ream On Leading The USA & His Hair Promise", duration: "40 mins"},
  {image: sportAudio04, section: "The Wayne Rooney Show", title: "EXPOSED: England’s Biggest World Cup Problem", duration: "43 mins"},
  {image: sportAudio05, section: "Rugby Union Weekly", title: "In Jo’burg with Sir Kev", duration: "39 mins"},
  {image: sportAudio06, section: "More than the Score", title: "World Cup 2026: What next for Germany after Paraguay exit?", duration: "25 mins"},
  {image: sportAudio07, section: "Test Match Special", title: "Ben Stokes: the man of the moments", duration: "37 mins"},
];

function PreviewAd({id, instanceName, template}: {id: GdnTemplateId; instanceName: string; template: ClientTemplate}) {
  const scale = DISPLAY_SCALE[id];

  return (
    <div
      className={`gdn-news-preview__ad gdn-news-preview__ad--${id}`}
      data-template-id={id}
      data-preview-instance={instanceName}
      aria-label={`${id} advertisement preview`}
      style={
        {
          "--gdn-news-ad-scale": scale,
          width: template.width * scale,
          height: template.height * scale,
        } as CSSProperties
      }
    >
      {template.getComponent()}
    </div>
  );
}

function StoryCard({story, compact = false}: {story: Story; compact?: boolean}) {
  return (
    <article className={compact ? "preview-story preview-story--compact" : "preview-story"}>
      <div className="preview-story__image">
        <img src={story.image} alt="" />
        {story.duration && story.duration.includes(":") ? <span>▶ {story.duration}</span> : null}
      </div>
      {story.section ? <small>{story.section}</small> : null}
      <h3>{story.title}</h3>
      {story.summary ? <p>{story.summary}</p> : null}
    </article>
  );
}

function TextLinks({items}: {items: readonly string[]}) {
  return <ul className="cnn-text-links">{items.map((item) => <li key={item}>{item}</li>)}</ul>;
}

function SectionLabel({children}: {children: ReactNode}) {
  return <h3 className="cnn-section-label">{children}</h3>;
}

function AudioStrip({title, items}: {title: string; items: Story[]}) {
  return (
    <section className="cnn-audio-strip">
      <div className="cnn-audio-strip__heading"><b>{title} ›</b><span>‹&nbsp;&nbsp;›</span></div>
      <div className="cnn-audio-strip__grid">
        {items.map((item) => (
          <article key={item.title}>
            <img src={item.image} alt="" />
            <small>{item.section}</small>
            <p>{item.title}</p>
            <span>▣&nbsp; Save&nbsp;&nbsp; {item.duration}</span>
          </article>
        ))}
      </div>
    </section>
  );
}

function PhotoGalleryCard({story}: {story: Story}) {
  return (
    <div className="cnn-photo-gallery-card">
      <StoryCard compact story={story} />
      <b>Gallery</b>
    </div>
  );
}

function AdChoicesMark() {
  return (
    <div className="gdn-ad-choices-mark" aria-hidden="true">
      <div className="gdn-ad-choices-mark__glyphs">
        <img className="gdn-ad-choices-mark__outline gdn-ad-choices-mark__outline--play" src={adChoiceOutline} alt="" />
        <img className="gdn-ad-choices-mark__outline gdn-ad-choices-mark__outline--menu" src={adChoiceOutline} alt="" />
        <img className="gdn-ad-choices-mark__center-dot" src={adChoiceDot} alt="" />
        <img className="gdn-ad-choices-mark__play" src={adChoicePlay} alt="" />
        <span className="gdn-ad-choices-mark__menu-dots">
          <img src={adChoiceMenuDot} alt="" />
          <img src={adChoiceMenuDot} alt="" />
          <img src={adChoiceMenuDot} alt="" />
        </span>
      </div>
    </div>
  );
}

function ResponsiveComposite({variant, children}: {variant: "square" | "landscape"; children: ReactNode}) {
  if (!children) return null;

  return (
    <div className={`gdn-responsive-composite gdn-responsive-composite--${variant}`} data-responsive-composite={variant}>
      {children}
      <div className="gdn-responsive-composite__copy">
        <strong>Some text</strong>
        {variant === "square" ? <span>as a text</span> : null}
        <button type="button">Learn more</button>
      </div>
      <AdChoicesMark />
    </div>
  );
}

function CnnHeader() {
  return (
    <header className="cnn-header">
      <button type="button" aria-label="Open menu">☰</button>
      <strong><i>CNN</i> World</strong>
      <nav aria-label="CNN sections">
        <span>World</span><span>Africa</span><span>Americas</span><span>Asia</span>
        <span>Australia</span><span>China</span><span>Europe</span><span>India</span>
        <span>Middle East</span><span>More</span>
      </nav>
      <div>Watch&nbsp;&nbsp; Listen&nbsp;&nbsp; Sign in</div>
    </header>
  );
}

function CnnWorldPage({ad}: {ad: AdRenderer}) {
  return (
    <article className="news-page news-page--cnn-world" data-preview-page="cnn-world">
      {ad("BillboardAds", "ad-section--cnn-top", "billboard-top")}
      <CnnHeader />
      <main className="cnn-world-shell">
        <h1>World</h1>

        <section className="cnn-world-lead">
          <div className="cnn-world-lead__primary">
            <StoryCard story={{image: world13, title: "US and Iranian officials are holding indirect, low-level talks in Qatar, source says"}} />
            <TextLinks items={["Who is the Ukrainian tycoon at the center of mysterious Monaco bombing?", "Tracking traffic in the Strait of Hormuz, in maps and charts", "China tells its ethnic minorities to integrate or face consequences with sweeping new unity law", "Roof of tutoring center collapses in eastern Pakistan, killing at least 14 children, police say"]} />
          </div>
          <div className="cnn-world-lead__briefs">
            <StoryCard compact story={{image: world03, title: "Pope Leo faces first major crisis of papacy from rebel Catholic group"}} />
            <TextLinks items={["She was a first lady at 19. Now she’s one step closer to winning Peru’s presidency", "Self-exiled Chinese billionaire gets 30 years in US jail for ‘astonishing’ fraud", "How to help those affected by the earthquakes in Venezuela", "Six killed in shooting at German youth facility, police say", "Russia is burning, but don’t expect Putin to blink"]} />
          </div>
          <div className="cnn-world-lead__rail">
            {ad("InlineRectangleAds", "ad-section--rectangle", "inline-top")}
            <StoryCard compact story={{image: world05, duration: "4:53", title: "Inside one of the most daring escapes from North Korea"}} />
            <StoryCard compact story={{image: world07, title: "Wife of Spanish PM forbidden to leave country as corruption probes pile up"}} />
          </div>
        </section>

        <section className="cnn-video-strip">
          <h2>Video</h2>
          <div>{moreStories.slice(0, 5).map((story) => <StoryCard compact key={story.title} story={story} />)}</div>
        </section>

        <section className="cnn-around">
          <h2>Around the world</h2>
          <div className="cnn-around__grid">
            <div><SectionLabel>Europe</SectionLabel><StoryCard story={{image: world02, title: "Ukraine launches major drone attack on Moscow, hitting a satellite center and killing a baby"}} /><TextLinks items={["Kate, Princess of Wales, climbs Britain’s three highest peaks in 24 hours to raise money for cancer hospital", "Skydiving plane crashes in France, killing 11 people", "Germans are researching their Nazi past as the far right urges them to move on"]} /></div>
            <div><SectionLabel>Middle East</SectionLabel><StoryCard story={{image: world20, title: "Netanyahu’s emerging challenger represents his polar opposite, and that may be his appeal"}} /><TextLinks items={["Israel’s military and tech industry race to counter Hezbollah’s latest threat", "Iran and US exchange strikes as Hormuz tensions stress agreement", "Israel to withdraw from two areas in Lebanon under newly signed agreement"]} /></div>
            <div className="cnn-around__rail">{ad("LargeRectangleAds", "ad-section--rectangle", "large-around-world")}<SectionLabel>World in photos</SectionLabel><StoryCard compact story={{image: world08, title: "The week in 37 photos"}} /></div>
          </div>
        </section>

        {ad("Leaderboard970Ads", "ad-section--wide", "leaderboard-970")}

        <section className="cnn-regional-grid">
          <div><SectionLabel>Europe</SectionLabel><StoryCard story={{image: world02, title: "Ukraine launches major drone attack on Moscow, hitting a satellite center and killing a baby"}} /></div>
          <div><SectionLabel>Americas</SectionLabel><StoryCard story={{image: more03, title: "Venezuelans scour collapsed buildings by hand while government machinery stands unused"}} /></div>
          <div><SectionLabel>Asia</SectionLabel><StoryCard story={{image: more11, title: "Australian man arrested after ‘best team get home’ demand in extortion"}} /></div>
        </section>

        <section className="cnn-featured">
          <h2>Featured</h2>
          <div className="cnn-editorial-grid">
            <div><SectionLabel>Travel</SectionLabel><StoryCard story={{image: world19, title: "A New England oasis with a century-old pizza scene"}} /><TextLinks items={["A Southern city’s ‘50-year overnight success story’", "An American steel town with serious Christmas spirit: ‘It’s like living in a snow globe’", "Fort Collins, Colorado: The real happiest place on Earth?"]} /></div>
            <div><SectionLabel>Style</SectionLabel><StoryCard story={{image: world14, title: "A soccer star’s Birkin collection is turning heads at the World Cup"}} /><TextLinks items={["At Men’s Fashion Week in Paris, a brutal heat wave disrupts the fantasy", "Every day is soccer jersey day", "This designer will do anything for his village"]} /></div>
            <div><SectionLabel>Weather</SectionLabel><StoryCard story={{image: world18, title: "Fast-moving wildfires scorch thousands of acres across the West, and there’s more danger ahead"}} /><TextLinks items={["Hottest temperatures in over a decade threatened in parts of eastern US", "US heat forecast: See where Americans are at the most risk for extreme heat"]} /></div>
          </div>
        </section>

        {ad("BillboardAds", "ad-section--billboard-middle", "billboard-middle")}
        <AudioStrip title="Recommended audio" items={recommendedAudio} />

        <section className="cnn-technology">
          <div className="cnn-technology__stories"><SectionLabel>Technology</SectionLabel><StoryCard story={{image: techPhone, section: "Technology", title: "Google Pixel 4A Review: At $350, a Win for Those on a Budget", summary: "Yes, you can now pay less than $399 for a smartphone — and it won’t stink"}} /><StoryCard story={{image: techLaptop, section: "Technology", title: "Livestream Your Wedding Like a Pro", summary: "These gadgets will help you improve the production quality and create memorable experiences for you and your digital guests."}} /><StoryCard story={{image: techChips, section: "Technology", title: "Why the next generation of chips may be built for AI first", summary: "New processors promise faster models, cooler data centres and longer battery life."}} /></div>
          <TextLinks items={["How to Fight Against Big Tech’s Power", "William English, Who Helped Build the Computer Mouse, Dies at 91", "Start-Ups Braced for the Worst. The Worst Never Came.", "A High-Tech Array of Travel Tools: ‘Smart’ Health Cards, Temperature-Reading Glasses and More", "Inside the race to build a quantum computer that actually works", "The tiny chips powering a new generation of medical devices", "Why repairable gadgets are finally moving into the mainstream", "Could satellites bring reliable internet to every rural home?"]} />
          {ad("HalfPageAds", "ad-section--half-page", "half-page-technology")}
        </section>

        <section className="cnn-more-latest">
          <h2>More of the latest stories</h2>
          <div className="cnn-more-latest__grid">{moreStories.map((story) => <StoryCard compact key={story.title} story={story} />)}</div>
          <div className="cnn-more-latest__columns">
            <div><SectionLabel>Europe</SectionLabel><StoryCard story={{image: world02, title: "Ukraine launches major drone attack on Moscow, hitting a satellite center and killing a baby"}} /><TextLinks items={["Kate, Princess of Wales, climbs Britain’s three highest peaks in 24 hours to raise money for cancer hospital", "Skydiving plane crashes in France, killing 11 people", "Germans are researching their Nazi past as the far right urges them to move on", "Drone, artillery attacks kill civilians on both sides of Russia-Ukraine border", "Serbian President Vucic says he will resign within weeks amid student-led protests"]} /></div>
            <div><SectionLabel>Middle East</SectionLabel><StoryCard story={{image: world20, title: "Netanyahu’s emerging challenger represents his polar opposite, and that may be his appeal"}} /><TextLinks items={["Israel’s military and tech industry race to counter Hezbollah’s latest threat", "Iran and US exchange strikes as Hormuz tensions stress agreement", "Israel to withdraw from two areas in Lebanon under newly signed agreement", "Iran strikes vessel, pausing UN efforts to evacuate ships from Hormuz", "Trump’s Gulf allies fear his Iran agreement is a ‘disastrous turning point’"]} /></div>
            <div className="cnn-more-latest__photo-rail">
              <SectionLabel>World in photos</SectionLabel>
              <PhotoGalleryCard story={{image: world08, title: "The week in 37 photos"}} />
              <PhotoGalleryCard story={{image: worldPeopleLost, title: "People we’ve lost in 2026"}} />
              <PhotoGalleryCard story={{image: world14, title: "Remarkable moments from around the world"}} />
            </div>
            {ad("WideSkyscraperAds", "ad-section--skyscraper", "wide-skyscraper")}
          </div>
        </section>

        <section className="cnn-something-extra">
          <h2>Something Extra</h2>
          <div className="cnn-editorial-grid">
            <div><SectionLabel>Architecture around the world</SectionLabel><StoryCard story={{image: extraArchitecture, title: "Tear your eyes away from the ballroom. This courthouse is the real face of Trump-era architecture"}} /><TextLinks items={["Sagrada Família’s 144-year journey nears its end with the Tower of Jesus Christ", "A first look inside the $850 million Obama Presidential Center", "Antarctica is architecture’s last frontier. One man is designing more of it than anyone else", "A piece of France’s Eiffel Tower is now up for grabs. You’ll need deep pockets"]} /></div>
            <div><SectionLabel>Sports</SectionLabel><StoryCard story={{image: extraSports, title: "Mexico batters Ecuador and everything else you need to know about Tuesday’s World Cup action"}} /><TextLinks items={["Serena Williams’ Wimbledon return felt special and yet strangely familiar", "LeBron James will leave the Lakers and hit the free agent market", "France is one of the heavy favorites to win the World Cup. This is what makes them so good", "When Erling Haaland’s youth coaches realized he was something special"]} /></div>
            <div><SectionLabel>Tech</SectionLabel><StoryCard story={{image: extraTech, title: "White House lifts export control on Anthropic that froze its most advanced models"}} /><TextLinks items={["China’s humanoid robots have captivated the world. A rental market is exposing their limits", "More than half of social media child safety features aren’t working as advertised, new research finds", "This anti-AI evangelist is growing more popular. That could be a problem for Trump", "US government allows Anthropic limited release of AI model that sparked cybersecurity concerns"]} /></div>
          </div>
        </section>

        {ad("Leaderboard728Ads", "ad-section--wide", "leaderboard-728")}

        <section className="cnn-news-grid">
          <div className="cnn-news-grid__list"><SectionLabel>Covid-19 news</SectionLabel>{[newsLeft01, newsLeft02, newsLeft03].map((image, index) => <StoryCard compact key={image} story={{image, title: ["8 Dead After Attack in a Wildlife Reserve in Niger", "Belarus Says Longtime Leader Is Re-elected in Vote Critics Call Rigged", "U.S. Official Praises Taiwan During Highest-Level Visit in Decades"][index]}} />)}</div>
          <StoryCard story={{image: newsHero, section: "World", title: "Goldman Says Time to Think About a Shift in Market Leadership", summary: "Curb cuts may seem like an obvious civic good now. But the protracted battle to enact them exposes a design history"}} />
          <div className="cnn-news-grid__right"><TextLinks items={["Clashes Erupt in Beirut at Blast Protest as Lebanon’s Anger Boils Over", "Beijing Launches Another Demolition Drive, This Time in Its Bucolic Suburbs", "Air India Crash Investigators Focus On a Dangerous Runway and a Pilot’s Actions", "Flood warnings issued as severe storms sweep across southern Europe", "UN urges immediate access to families displaced by border fighting"]} />{ad("GdnJapanAds", "ad-section--japan", "gdn-japan")}</div>
        </section>

        <AudioStrip title="Latest sport audio" items={sportAudio} />
        {ad("Leaderboard468Ads", "ad-section--compact", "leaderboard-468")}

        <section className="cnn-lower-columns">
          <div><SectionLabel>US &amp; Canada news ›</SectionLabel><img className="cnn-lower-columns__image" src={lowerUs} alt="" /><h3>World Cup fans sweat through US ‘heat dome’</h3><p>As scorching heat left the US east coast baking in the middle of the World Cup, the BBC caught up with fans braving the weather to support their team.</p><TextLinks items={["What we know about Taylor Swift’s rumoured NYC wedding celebrations", "US Supreme Court upholds birthright citizenship in blow to Trump", "US Supreme Court upholds bans on transgender athletes in female school and college sports"]} /></div>
          <div><SectionLabel>More World News ›</SectionLabel><img className="cnn-lower-columns__image" src={lowerWorld} alt="" /><h3>What is Ebola and why is stopping the latest outbreak so difficult?</h3><p>An outbreak of Ebola in DR Congo involves a rare species of the virus and is in an area affected by conflict.</p><TextLinks items={["At least five killed in fire at Antwerp apartment block, say police", "Farming worries after India records driest June in over a decade", "Sudan’s RSF committed crimes against humanity in el-Fasher, Amnesty says"]} /></div>
          <div><SectionLabel>Science &amp; Health ›</SectionLabel><img className="cnn-lower-columns__image" src={lowerScience} alt="" /><h3>Breakthrough blood test could spot disease years before symptoms appear</h3><p>Researchers say the new screening method could help doctors identify early warning signs and begin treatment sooner.</p><TextLinks items={["Scientists map the hidden microbes that keep cities healthy", "Why sleep may be the brain’s most powerful repair tool", "New satellite tracks air pollution street by street"]} /></div>
        </section>

        <ResponsiveComposite variant="square">
          {ad("GdnResponsive1200x1200Ads", "gdn-responsive-composite__creative", "responsive-square")}
        </ResponsiveComposite>

        <section className="cnn-lower-columns cnn-lower-columns--business">
          <div><SectionLabel>Business ›</SectionLabel><StoryCard story={{image: newsLeft03, title: "Up to 150 ex-WHSmith High Street stores to close as rescue deal approved"}} /><TextLinks items={["What will the energy cap changes mean for my bills?", "Why is crucial tech vulnerable to the heat?", "What you need to know as ScotRail starts charging ticket dodgers minimum £10 fare"]} /></div>
          <div><SectionLabel>Technology ›</SectionLabel><StoryCard story={{image: world03, title: "I’m in therapy for my 14-hour-a-day phone addiction and I’m determined to beat it"}} /><TextLinks items={["Tech firms are blaming AI for mega device and console price rises", "GTA 6 will launch as download only - does that mean the disc is dead?", "The abundant but expensive energy source that’s under your feet"]} /></div>
          <div><SectionLabel>Culture ›</SectionLabel><StoryCard story={{image: lowerCulture, title: "Inside the studios turning discarded materials into bold new art"}} /><TextLinks items={["The young designers bringing craft back to the high street", "Five exhibitions changing how we see the modern city", "Why independent cinemas are finding a new generation of fans"]} /></div>
        </section>

        <ResponsiveComposite variant="landscape">
          {ad("GdnResponsive1200x628Ads", "gdn-responsive-composite__creative", "responsive-landscape")}
        </ResponsiveComposite>
      </main>

      <footer className="cnn-footer"><input aria-label="Search CNN" placeholder="Search CNN..." /><div><strong>CNN World</strong><span>Watch&nbsp;&nbsp;&nbsp; Listen&nbsp;&nbsp;&nbsp; Live TV&nbsp;&nbsp;&nbsp; Sign in</span></div><p>FOLLOW CNN&nbsp;&nbsp; ◼︎ &nbsp;𝕏&nbsp; ◎ &nbsp;◉ &nbsp;in&nbsp;&nbsp; | &nbsp;&nbsp;DOWNLOAD THE CNN APP</p><small>Terms of Use&nbsp;&nbsp;&nbsp; Privacy Policy&nbsp;&nbsp;&nbsp; Manage Cookies&nbsp;&nbsp;&nbsp; Ad Choices&nbsp;&nbsp;&nbsp; Accessibility &amp; CC&nbsp;&nbsp;&nbsp; About&nbsp;&nbsp;&nbsp; Newsletters&nbsp;&nbsp;&nbsp; Transcripts&nbsp;&nbsp;&nbsp; Sitemap</small></footer>
    </article>
  );
}

type GdnNewsPreviewProps = {
  onClose: () => void;
  creativeClassName: string;
  baseColor: string;
};

export default function GdnNewsPreview({
  onClose,
  creativeClassName,
  baseColor,
}: GdnNewsPreviewProps) {
  const {visible, layoutOption} = useSetting();
  const templateSelection = useTemplateSelection();

  const selectedTemplates = useMemo(() => {
    if (!Array.isArray(visible)) throw new Error("GDN preview requires a visible template list");

    const layout = Number(layoutOption);
    const gdnTemplates = (drawnClientTemplates("ads", templateSelection) as ClientTemplate[]).filter((template) => template.contentTypes?.includes("google-display-network") && (!template.layouts || template.layouts.includes(layout)));
    const visibleIds = new Set<string>(visible as string[]);
    const selected = gdnTemplates.filter((template) => visibleIds.has(template.id));
    const unplacedTemplate = selected.find((template) => !TEMPLATE_ID_SET.has(template.id));
    if (unplacedTemplate) throw new Error(`GDN preview has no placement for template "${unplacedTemplate.id}"`);
    return new Map(selected.map((template) => [template.id, template]));
  }, [layoutOption, visible, templateSelection]);

  useEffect(() => {
    document.body.classList.add("gdn-news-preview-open");
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      onClose();
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.classList.remove("gdn-news-preview-open");
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [onClose]);

  const ad: AdRenderer = (id, className = "", instanceName = id) => {
    const template = selectedTemplates.get(id);
    if (!template) return null;
    return (
      <section className={`gdn-news-preview__ad-section ${className}`.trim()} data-ad-section-for={id} data-preview-instance={instanceName} aria-label={`${id} reserved section`}>
        <PreviewAd id={id} instanceName={instanceName} template={template} />
      </section>
    );
  };

  return (
    <div
      className={`App gdn-news-preview${creativeClassName}`}
      style={{"--base-color": baseColor} as CSSProperties}
    >
      <header className="gdn-news-preview__controls"><button type="button" onClick={onClose} aria-label="Back to GDN templates"><span aria-hidden="true">←</span> Back</button><span>Website preview</span></header>
      <CnnWorldPage ad={ad} />
      {selectedTemplates.size === 0 ? <div className="gdn-news-preview__empty">Select at least one template in the editor to see it on this page.</div> : null}
    </div>
  );
}
